import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import {
  countryLanguageMappingsApi,
  regulatoryBundlesApi,
  voiceAgentsApi,
  voiceCallsApi,
} from '../api';
import type {
  AudioUrlResponse,
  CallStats,
  CreateCountryLanguageMappingDto,
  CreateVoiceAgentDto,
  KnowledgeBaseItem,
  ListVoiceAgentsFilters,
  ListVoiceCallsFilters,
  PaginatedVoiceCalls,
  ReplayData,
  ReprocessAnalysisResponse,
  TriggerBatchVoiceCallDto,
  TriggerVoiceCallDto,
  UpdateCountryLanguageMappingDto,
  UpdateVoiceAgentDto,
  UpdateVoiceCallDto,
  UploadKnowledgeItemDto,
  VoiceAgent,
  VoiceCall,
  VoiceCallBatch,
  VoiceCallBatchStatus,
  VoiceCallStatus,
  VoiceCallTranscriptEntry,
} from '../types';

const IN_PROGRESS_STATUSES: VoiceCallStatus[] = [
  'dialing',
  'detecting',
  'disclosure',
  'discovery',
  'handling',
  'cta',
  'closing',
];

function isInProgress(call: VoiceCall): boolean {
  return IN_PROGRESS_STATUSES.includes(call.status);
}

const CALL_KEYS = {
  all: ['voice-calls'] as const,
  list: (orgId: string, filters?: ListVoiceCallsFilters) =>
    [...CALL_KEYS.all, 'list', orgId, filters ?? {}] as const,
  detail: (id: string) => [...CALL_KEYS.all, 'detail', id] as const,
  transcript: (id: string) => [...CALL_KEYS.all, 'transcript', id] as const,
  audio: (id: string) => [...CALL_KEYS.all, 'audio', id] as const,
  replay: (id: string) => [...CALL_KEYS.all, 'replay', id] as const,
  stats: (orgId: string) => [...CALL_KEYS.all, 'stats', orgId] as const,
  batch: (id: string) => [...CALL_KEYS.all, 'batch', id] as const,
};

const TERMINAL_BATCH_STATUSES: VoiceCallBatchStatus[] = ['completed', 'cancelled'];

const KEYS = {
  all: ['voice-agents'] as const,
  list: (orgId: string, filters?: ListVoiceAgentsFilters) =>
    [...KEYS.all, 'list', orgId, filters ?? {}] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  versions: (id: string) => [...KEYS.all, 'versions', id] as const,
};

/**
 * Fold a mutation response into the cached agent detail (FLOSEAI-659).
 *
 * The detail endpoint returns the agent WITH its `knowledgeItems`; create,
 * update, rename and publish return only the agent's own columns. Writing one
 * straight over the other dropped the knowledge list from cache, and a 30s
 * staleTime meant nothing went back to the server to notice — so the Knowledge
 * tab read as empty until some other mutation invalidated the query.
 *
 * Merging keeps whatever richer data the detail already held. None of these
 * mutations touch knowledge, so the cached list is still the truth.
 */
function mergeIntoAgentDetail(queryClient: QueryClient, agent: VoiceAgent) {
  queryClient.setQueryData<VoiceAgent>(KEYS.detail(agent.id), (previous) =>
    previous ? { ...previous, ...agent } : agent,
  );
}

export function useVoiceAgents(organizationId: string, filters?: ListVoiceAgentsFilters) {
  return useQuery({
    queryKey: KEYS.list(organizationId, filters),
    queryFn: () => voiceAgentsApi.list(organizationId, filters),
    enabled: !!organizationId,
    staleTime: 30_000,
  });
}

// `language` is part of the key, not just the request: switching the agent
// language in the wizard has to refetch the curated copy, the same way
// useVoiceLibrary refetches the voices (FLOSEAI-663).
export function useVoiceAgentTemplates(language?: string) {
  return useQuery({
    queryKey: [...KEYS.all, 'templates', language ?? 'default'] as const,
    queryFn: () => voiceAgentsApi.listTemplates(language),
    staleTime: 5 * 60_000,
  });
}

export function useVoiceAgentScriptVariables(useCase?: string, language?: string) {
  return useQuery({
    queryKey: [...KEYS.all, 'script-variables', useCase ?? 'all', language ?? 'default'] as const,
    queryFn: () => voiceAgentsApi.listScriptVariables(useCase, language),
    staleTime: 10 * 60_000,
  });
}

export function useVoiceLibrary(language?: string) {
  return useQuery({
    queryKey: [...KEYS.all, 'voices', language ?? 'all'] as const,
    queryFn: () => voiceAgentsApi.listVoices(language),
    staleTime: 10 * 60_000,
  });
}

export function useVoiceAgentAnalytics(
  id: string,
  organizationId: string,
  period: import('../types').AnalyticsPeriod,
) {
  return useQuery({
    queryKey: [...KEYS.all, 'analytics', id, organizationId, period] as const,
    queryFn: () => voiceAgentsApi.getAnalytics(id, organizationId, period),
    enabled: !!id && !!organizationId,
    staleTime: 5 * 60_000,
  });
}

const COUNTRY_LANG_KEY = ['country-language-mappings'] as const;

export function useCountryLanguageMappings() {
  return useQuery({
    queryKey: COUNTRY_LANG_KEY,
    queryFn: () => countryLanguageMappingsApi.list(),
    staleTime: 60_000,
  });
}

export function useCreateCountryLanguageMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateCountryLanguageMappingDto) => countryLanguageMappingsApi.create(dto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: COUNTRY_LANG_KEY }),
  });
}

export function useUpdateCountryLanguageMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      countryCode,
      dto,
    }: {
      countryCode: string;
      dto: UpdateCountryLanguageMappingDto;
    }) => countryLanguageMappingsApi.update(countryCode, dto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: COUNTRY_LANG_KEY }),
  });
}

export function useDeleteCountryLanguageMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (countryCode: string) => countryLanguageMappingsApi.remove(countryCode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: COUNTRY_LANG_KEY }),
  });
}

const REGULATORY_KEY = (orgId: string) => ['regulatory-bundles', orgId] as const;
const REGULATORY_PENDING_KEY = ['regulatory-bundles', 'pending'] as const;

export function useRegulatoryBundles(organizationId: string) {
  return useQuery({
    queryKey: REGULATORY_KEY(organizationId),
    queryFn: () => regulatoryBundlesApi.list(organizationId),
    enabled: !!organizationId,
    staleTime: 30_000,
  });
}

export function useRegulatoryBundlesPending() {
  return useQuery({
    queryKey: REGULATORY_PENDING_KEY,
    queryFn: () => regulatoryBundlesApi.listPending(),
    staleTime: 30_000,
  });
}

export function useUploadRegulatoryBundle(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: Parameters<typeof regulatoryBundlesApi.upload>[1]) =>
      regulatoryBundlesApi.upload(organizationId, args),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: REGULATORY_KEY(organizationId) }),
  });
}

export function useUpdateRegulatoryBundleStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof regulatoryBundlesApi.updateStatus>[1];
    }) => regulatoryBundlesApi.updateStatus(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['regulatory-bundles'] }),
  });
}

export function useVoiceAgent(id: string, organizationId: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => voiceAgentsApi.get(id, organizationId),
    enabled: !!id && !!organizationId,
    staleTime: 30_000,
  });
}

export function useCreateVoiceAgent() {
  const queryClient = useQueryClient();

  return useMutation<VoiceAgent, Error, { organizationId: string; dto: CreateVoiceAgentDto }>({
    mutationFn: ({ organizationId, dto }) => voiceAgentsApi.create(organizationId, dto),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      mergeIntoAgentDetail(queryClient, agent);
    },
  });
}

export function useUpdateVoiceAgent() {
  const queryClient = useQueryClient();

  return useMutation<
    VoiceAgent,
    Error,
    { id: string; organizationId: string; dto: UpdateVoiceAgentDto }
  >({
    mutationFn: ({ id, organizationId, dto }) => voiceAgentsApi.update(id, organizationId, dto),
    onSuccess: (agent) => {
      mergeIntoAgentDetail(queryClient, agent);
    },
  });
}

/**
 * FLOSEAI-656 — invalidates the LIST as well as the detail, because the whole
 * point of renaming is that the new name shows up in the agent pickers: the
 * trigger-call dropdown, the batch-call dropdown and the calls filter all read
 * that list. `useUpdateVoiceAgent` only writes the detail cache, which would
 * leave the user renaming an agent and still seeing the old name where it
 * mattered.
 */
export function useRenameVoiceAgent() {
  const queryClient = useQueryClient();

  return useMutation<VoiceAgent, Error, { id: string; organizationId: string; name: string }>({
    mutationFn: ({ id, organizationId, name }) => voiceAgentsApi.rename(id, organizationId, name),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      mergeIntoAgentDetail(queryClient, agent);
    },
  });
}

export function usePublishVoiceAgent() {
  const queryClient = useQueryClient();

  return useMutation<VoiceAgent, Error, { id: string; organizationId: string }>({
    mutationFn: ({ id, organizationId }) => voiceAgentsApi.publish(id, organizationId),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      mergeIntoAgentDetail(queryClient, agent);
    },
  });
}

export function useArchiveVoiceAgent() {
  const queryClient = useQueryClient();

  return useMutation<VoiceAgent, Error, { id: string; organizationId: string }>({
    mutationFn: ({ id, organizationId }) => voiceAgentsApi.archive(id, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDuplicateVoiceAgent() {
  const queryClient = useQueryClient();

  return useMutation<VoiceAgent, Error, { id: string; organizationId: string }>({
    mutationFn: ({ id, organizationId }) => voiceAgentsApi.duplicate(id, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteVoiceAgent() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; organizationId: string }>({
    mutationFn: ({ id, organizationId }) => voiceAgentsApi.remove(id, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useVoiceAgentVersions(id: string, organizationId: string) {
  return useQuery({
    queryKey: KEYS.versions(id),
    queryFn: () => voiceAgentsApi.listVersions(id, organizationId),
    enabled: !!id && !!organizationId,
    staleTime: 60_000,
  });
}

export function useUploadKnowledgeItem() {
  const queryClient = useQueryClient();

  return useMutation<
    KnowledgeBaseItem,
    Error,
    { agentId: string; organizationId: string; dto: UploadKnowledgeItemDto }
  >({
    mutationFn: ({ agentId, organizationId, dto }) =>
      voiceAgentsApi.uploadKnowledgeItem(agentId, organizationId, dto),
    onSuccess: (_item, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: KEYS.detail(agentId) });
    },
  });
}

export function useUploadKnowledgePdf() {
  const queryClient = useQueryClient();

  return useMutation<
    KnowledgeBaseItem,
    Error,
    { agentId: string; organizationId: string; formData: FormData }
  >({
    mutationFn: ({ agentId, organizationId, formData }) =>
      voiceAgentsApi.uploadKnowledgePdf(agentId, organizationId, formData),
    onSuccess: (_item, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: KEYS.detail(agentId) });
    },
  });
}

export function useRemoveKnowledgeItem() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { agentId: string; itemId: string; organizationId: string }>({
    mutationFn: ({ agentId, itemId, organizationId }) =>
      voiceAgentsApi.removeKnowledgeItem(agentId, itemId, organizationId),
    onSuccess: (_data, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: KEYS.detail(agentId) });
    },
  });
}

export function useLatestPreviewSession(agentId: string) {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id ?? '';

  return useQuery<import('../types').LatestPreviewSession | null, Error>({
    queryKey: ['preview-latest', agentId] as const,
    queryFn: () => voiceAgentsApi.getLatestPreviewSession(agentId, orgId),
    enabled: !!agentId && !!orgId,
    staleTime: 0,
  });
}

export function usePreviewSessions(agentId: string) {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id ?? '';

  return useQuery<import('../types').LatestPreviewSession[], Error>({
    queryKey: ['preview-sessions', agentId] as const,
    queryFn: () => voiceAgentsApi.listPreviewSessions(agentId, orgId),
    enabled: !!agentId && !!orgId,
    staleTime: 0,
  });
}

export function useVoiceCalls(organizationId: string, filters?: ListVoiceCallsFilters) {
  return useQuery<PaginatedVoiceCalls, Error>({
    queryKey: CALL_KEYS.list(organizationId, filters),
    queryFn: () => voiceCallsApi.list(organizationId, filters),
    enabled: !!organizationId,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.data.some(isInProgress)) return 10_000;
      return false;
    },
  });
}

export function useVoiceCall(id: string, organizationId: string) {
  return useQuery<VoiceCall, Error>({
    queryKey: CALL_KEYS.detail(id),
    queryFn: () => voiceCallsApi.get(id, organizationId),
    enabled: !!id && !!organizationId,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && isInProgress(data)) return 10_000;
      return false;
    },
  });
}

export function useVoiceCallTranscript(id: string, organizationId: string) {
  return useQuery<VoiceCallTranscriptEntry[], Error>({
    queryKey: CALL_KEYS.transcript(id),
    queryFn: () => voiceCallsApi.getTranscript(id, organizationId),
    enabled: !!id && !!organizationId,
    staleTime: 30_000,
  });
}

export function useVoiceCallAudio(id: string, organizationId: string) {
  return useQuery<AudioUrlResponse, Error>({
    queryKey: CALL_KEYS.audio(id),
    queryFn: () => voiceCallsApi.getAudio(id, organizationId),
    enabled: !!id && !!organizationId,
    staleTime: 9 * 60 * 1000,
  });
}

export function useVoiceCallReplay(id: string, organizationId: string) {
  return useQuery<ReplayData, Error>({
    queryKey: CALL_KEYS.replay(id),
    queryFn: () => voiceCallsApi.getReplay(id, organizationId),
    enabled: !!id && !!organizationId,
    staleTime: 60_000,
  });
}

export function useCallStats(organizationId: string) {
  return useQuery<CallStats, Error>({
    queryKey: CALL_KEYS.stats(organizationId),
    queryFn: () => voiceCallsApi.getStats(organizationId),
    enabled: !!organizationId,
    staleTime: 30_000,
  });
}

export function useCancelVoiceCall() {
  const queryClient = useQueryClient();

  return useMutation<{ cancelled: boolean }, Error, { id: string; organizationId: string }>({
    mutationFn: ({ id, organizationId }) => voiceCallsApi.cancel(id, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
    },
  });
}

export function useRetryVoiceCall() {
  const queryClient = useQueryClient();

  return useMutation<VoiceCall, Error, { id: string; organizationId: string }>({
    mutationFn: ({ id, organizationId }) => voiceCallsApi.retry(id, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
    },
  });
}

export function useTriggerVoiceCall() {
  const queryClient = useQueryClient();

  return useMutation<VoiceCall, Error, TriggerVoiceCallDto>({
    mutationFn: (dto) => voiceCallsApi.trigger(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
    },
  });
}

export function useDeleteVoiceCall() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; organizationId: string }>({
    mutationFn: ({ id, organizationId }) => voiceCallsApi.remove(id, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
    },
  });
}

export function useUpdateVoiceCall() {
  const queryClient = useQueryClient();

  return useMutation<
    VoiceCall,
    Error,
    { id: string; organizationId: string; leadId?: string; dto: UpdateVoiceCallDto }
  >({
    mutationFn: ({ id, organizationId, dto }) => voiceCallsApi.update(id, organizationId, dto),
    onSuccess: (call, { leadId }) => {
      queryClient.setQueryData(CALL_KEYS.detail(call.id), call);
      queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
      if (leadId) {
        queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      }
    },
  });
}

export function useTriggerBatchVoiceCall() {
  const queryClient = useQueryClient();

  return useMutation<VoiceCallBatch, Error, { organizationId: string } & TriggerBatchVoiceCallDto>({
    mutationFn: ({ organizationId, ...dto }) => voiceCallsApi.triggerBatch(organizationId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
    },
  });
}

export function useVoiceCallBatch(id: string, organizationId: string) {
  return useQuery<VoiceCallBatch, Error>({
    queryKey: CALL_KEYS.batch(id),
    queryFn: () => voiceCallsApi.getBatch(id, organizationId),
    enabled: !!id && !!organizationId,
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && !TERMINAL_BATCH_STATUSES.includes(data.status)) return 5000;
      return false;
    },
  });
}

export function useCancelVoiceCallBatch() {
  const queryClient = useQueryClient();

  return useMutation<{ cancelled: boolean }, Error, { id: string; organizationId: string }>({
    mutationFn: ({ id, organizationId }) => voiceCallsApi.cancelBatch(id, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
    },
  });
}

export function useReprocessCallAnalysis() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation<
    ReprocessAnalysisResponse,
    Error,
    { id: string; organizationId: string; leadId?: string }
  >({
    mutationFn: ({ id, organizationId }) => voiceCallsApi.reprocessAnalysis(id, organizationId),
    onSuccess: (_res, { id, leadId }) => {
      queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
      if (leadId) {
        queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      }
      trackEvent(mp, 'voice.call_analysis_reprocessed', { call_id: id });
    },
  });
}
